import React, { useState, useCallback } from 'react';
import { Button } from '@hwm/ui-lib';
import { Trans } from '@lingui/macro';
import { Menu, Typography } from '@material-ui/core';
import { ArrowDropUp } from '@material-ui/icons';
import { PMZoneSiteModel } from '@shared/models';

interface GraphSiteInfoProps {
  sitesData: PMZoneSiteModel[];
  /**
   * `dataTestId` - name of a Graph Site Info (preferably `<graph_name>-site-info`)
   * @example
   * ```tsx
   * <GraphSiteInfo {...props} dataTestId="test-graph-site-info"
   */
  dataTestId: string;
}

export const GraphSiteInfo: React.FC<GraphSiteInfoProps> = ({ sitesData, dataTestId }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const siteInfoComponent = useCallback(
    ({ id, siteName, address, siteType, smsNumber, serialNumber }: PMZoneSiteModel, i: number) => {
      return (
        <div key={id} className={sitesData?.length > 1 ? 'mb-2' : ''} data-testid={`${dataTestId}-site-${i}`}>
          <Typography variant="body1" style={{ marginRight: 8 }} data-testid={`${dataTestId}-site-${i}-name-address`}>
            <b>{siteName}</b>, {address}
          </Typography>
          <div className={`flex flex-wrap`}>
            <Typography variant="body2" style={{ marginRight: 8 }} data-testid={`${dataTestId}-site-${i}-device-type`}>
              <b>
                <Trans>Device Type</Trans>
              </b>
              : <Typography variant="caption">{siteType}</Typography>
            </Typography>
            <Typography variant="body2" style={{ marginRight: 8 }} data-testid={`${dataTestId}-site-${i}-sms`}>
              <b>
                <Trans>SMS</Trans>
              </b>
              : <Typography variant="caption">{smsNumber}</Typography>
            </Typography>
            <Typography variant="body2" style={{ marginRight: 8 }} data-testid={`${dataTestId}-site-${i}-serial`}>
              <b>
                <Trans>Serial</Trans>
              </b>
              : <Typography variant="caption">{serialNumber}</Typography>
            </Typography>
          </div>
        </div>
      );
    },
    [sitesData?.length, dataTestId]
  );

  return (
    <>
      <Button
        style={{ height: 28, padding: 8, width: 124, maxWidth: 124 }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
        endIcon={
          <ArrowDropUp
            style={{
              fontSize: 24,
              transform: anchorEl ? '' : 'rotate(180deg)',
            }}
          />
        }
        dataTestId={`${dataTestId}-sites-button`}
      >
        <Trans>Graph sites</Trans>
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{ paper: 'px-4 pt-2' }}
      >
        <div className="flex flex-col" data-testid={`${dataTestId}-sites-list`}>
          {sitesData.map(siteInfoComponent)}
        </div>
      </Menu>
    </>
  );
};
